import React, { useState, useEffect, useMemo } from "react";

import { toast } from 'react-toastify';
import { 
  FaSearch,
  FaTrash,
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFile,
  FaUpload,
  FaClipboardList,
  FaExternalLinkAlt,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFileVideo
} from 'react-icons/fa';

import { getProjectDocuments, uploadProjectDocumentV1, deleteProjectDocumentV1 } from '../../api/documents';
import { getTableHeaderStyle, DesignSystem } from '../../styles/designSystem';
import { DocumentUploadModal } from '../DocumentUploadModal';

// Updated interface based on actual API response
interface DocumentData {
  id: string;
  name: string;
  file_size: number;
  content_type: string;
  description?: string;
  document_kind: string;
  version: number;
  url: string;
  created_at: string;
  updated_at?: string;
  owner: {
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    id: string;
  };
}

interface TabDocumentsProps {
  projectId: string | undefined;
  token: string | undefined;
}

export const TabDocuments = ({ 
  projectId, 
  token
}: TabDocumentsProps) => {
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // Scroll detection for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load documents on mount
  useEffect(() => {
    if (projectId && token) {
      loadDocuments();
    }
  }, [projectId, token]);

  // Load documents from API
  const loadDocuments = async () => {
    if (!token || !projectId) return;
    
    try {
      setIsLoading(true);
      console.log('Loading documents for project:', projectId);
      
      const response = await getProjectDocuments(token, projectId);
      console.log('Documents API response:', response);
      
      if (response.results) {
        // Cast to DocumentData[] since we know the actual API structure
        const documents = response.results as any as DocumentData[];
        // Sort by created_at descending (newest first)
        const sortedDocs = documents.sort((a, b) => 
          new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
        );
        setDocuments(sortedDocs);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;
    
    const query = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(query) ||
      doc.document_kind.toLowerCase().includes(query) ||
      doc.content_type.toLowerCase().includes(query) ||
      doc.owner.first_name.toLowerCase().includes(query) ||
      doc.owner.last_name.toLowerCase().includes(query)
    );
  }, [documents, searchQuery]);

  // Get source type display name
  const getSourceDisplay = (document_kind: string) => {
    switch (document_kind) {
      case 'Task': return 'Project Item';
      case 'Action Item': return 'Action Item';
      case 'Meeting Note': return 'Meeting Note';
      case 'Daily Update': return 'Daily Update';
      default: return 'Project Document';
    }
  };

  // Get file type icon
  const getFileIcon = (_fileName: string, contentType: string) => {
    if (contentType.includes('pdf')) {
      return <FaFilePdf className="w-4 h-4 text-red-500" />;
    } else if (contentType.includes('word')) {
      return <FaFileWord className="w-4 h-4 text-blue-500" />;
    } else if (contentType.includes('excel') || contentType.includes('spreadsheet')) {
      return <FaFileExcel className="w-4 h-4 text-green-500" />;
    } else if (contentType.includes('powerpoint') || contentType.includes('presentation')) {
      return <FaFilePowerpoint className="w-4 h-4 text-orange-500" />;
    } else if (contentType.includes('image')) {
      return <FaFileImage className="w-4 h-4 text-purple-500" />;
    } else if (contentType.includes('video')) {
      return <FaFileVideo className="w-4 h-4 text-red-500" />;
    } else if (contentType.includes('text')) {
      return <FaFileAlt className="w-4 h-4 text-green-500" />;
    } else {
      return <FaFile className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get file type display name
  const getFileType = (fileName: string, contentType: string) => {
    if (contentType.includes('pdf')) return 'PDF';
    if (contentType.includes('word')) return 'Word';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'Excel';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'PowerPoint';
    if (contentType.includes('image')) return 'Image';
    if (contentType.includes('video')) return 'Video';
    if (contentType.includes('text')) return 'Text';
    
    const ext = fileName.split('.').pop()?.toUpperCase();
    return ext || 'File';
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle document download
  const handleDownload = async (document: DocumentData) => {
    if (!token || !document.url) {
      toast.error('Download URL not available');
      return;
    }
    
    try {
      console.log('Downloading document:', document.name, 'URL:', document.url);
      
      const response = await fetch(document.url, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Check if file can be opened in browser
      const contentType = response.headers.get('content-type') || document.content_type;
      const openableTypes = ['pdf', 'image', 'text'];
      const canOpen = openableTypes.some(type => 
        contentType.toLowerCase().includes(type)
      );

      if (canOpen) {
        window.open(url, '_blank');
      } else {
        // Download file
        const a = window.document.createElement('a');
        a.href = url;
        a.download = document.name;
        window.document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        window.document.body.removeChild(a);
      }
      
      toast.success('Document opened successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download document');
    }
  };

  // Handle file upload from modal
  const handleUploadFromModal = async (file: File) => {
    if (!token || !projectId) return;
    
    await uploadProjectDocumentV1(
      token, 
      projectId, 
      file, 
      '' // description - можно добавить поле в модальное окно позже
    );
    
    await loadDocuments();
  };

  // Handle document deletion
  const handleDelete = async (document: DocumentData) => {
    if (!token || !projectId) return;
    
    const confirmDelete = window.confirm(`Are you sure you want to delete "${document.name}"?`);
    if (!confirmDelete) return;

    try {
      await deleteProjectDocumentV1(token, projectId);
      toast.success('Document deleted successfully');
      await loadDocuments();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete document');
    }
  };



  return (
    <div className="min-h-screen bg-white">
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div className="sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredDocuments.length} of {documents.length} documents
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Upload Button */}
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                  >
                    <FaUpload className="w-3.5 h-3.5" />
                    <span>Upload</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="flex-1" role="columnheader">Document</div>
                <div className="mx-6 w-24" role="columnheader">Type</div>
                <div className="mx-6 w-32" role="columnheader">Source</div>
                <div className="mx-6 w-40" role="columnheader">Owner</div>
                <div className="mx-6 w-20" role="columnheader">Size</div>
                <div className="ml-6 w-20" role="columnheader">Actions</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Scrollable Content */}
      <div className="max-w-[1280px] mx-auto px-0 -mt-4">
        <div className="bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
              <span className="ml-3 text-sm text-gray-600">Loading documents...</span>
            </div>
          ) : filteredDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FaClipboardList className="w-12 h-12 mb-4 text-gray-300" />
              {searchQuery ? (
                <>
                  <h3 className="text-lg font-medium mb-2">No documents found</h3>
                  <p>Try adjusting your search query</p>
                </>
              ) : (
                <>
                  <h3 className="text-lg font-medium mb-2">No documents yet</h3>
                  <p className="mb-4">Upload your first document to get started</p>
                  <button
                    onClick={() => setIsUploadModalOpen(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaUpload className="w-4 h-4" />
                    <span>Upload Document</span>
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="bg-white">
              {filteredDocuments.map((document) => (
                <DocumentRow 
                  key={document.id} 
                  document={document} 
                  onDownload={handleDownload}
                  onDelete={handleDelete}
                  getFileIcon={getFileIcon}
                  getFileType={getFileType}
                  getSourceDisplay={getSourceDisplay}
                  formatFileSize={formatFileSize}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUploadFromModal}
        title="Upload Project Document"
        maxFileSize={10}
        description="Select a document or drag and drop it here"
      />
    </div>
  );
};

const DocumentRow = ({ 
  document, 
  onDownload, 
  onDelete,
  getFileIcon,
  getFileType,
  getSourceDisplay,
  formatFileSize
}: {
  document: DocumentData;
  onDownload: (doc: DocumentData) => void;
  onDelete: (doc: DocumentData) => void;
  getFileIcon: (fileName: string, contentType: string) => React.ReactElement;
  getFileType: (fileName: string, contentType: string) => string;
  getSourceDisplay: (document_kind: string) => string;
  formatFileSize: (bytes: number) => string;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`group hover:bg-gray-50/50 transition-all duration-200 ${
        isHovered ? 'bg-gray-50/50' : ''
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
        {/* Document Info */}
        <div className="flex-1 flex items-center space-x-3 min-w-0">
          {getFileIcon(document.name, document.content_type)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900 truncate">
                {document.name}
              </span>
              <span className="text-xs text-gray-500 flex-shrink-0">
                v{document.version}
              </span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span className="flex-shrink-0">{formatDate(document.created_at)}</span>
              {document.description && (
                <span className="truncate">• {document.description}</span>
              )}
            </div>
          </div>
        </div>

        {/* Type */}
        <div className="mx-6 w-24 flex items-center">
          <span className="text-sm text-gray-700 truncate">
            {getFileType(document.name, document.content_type)}
          </span>
        </div>

        {/* Source */}
        <div className="mx-6 w-32 flex items-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {getSourceDisplay(document.document_kind)}
          </span>
        </div>

        {/* Owner */}
        <div className="mx-6 w-40 flex items-center">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-medium text-gray-600">
                {document.owner.first_name.charAt(0)}{document.owner.last_name.charAt(0)}
              </span>
            </div>
            <span className="text-sm text-gray-700 truncate">
              {document.owner.first_name} {document.owner.last_name}
            </span>
          </div>
        </div>

        {/* Size */}
        <div className="mx-6 w-20 flex items-center">
          <span className="text-sm text-gray-500">
            {formatFileSize(document.file_size)}
          </span>
        </div>

        {/* Actions */}
        <div className="ml-6 w-20 flex items-center justify-end space-x-2">
          <button
            onClick={() => onDownload(document)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-all duration-200"
            title="Download"
          >
            <FaExternalLinkAlt className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(document)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all duration-200"
            title="Delete"
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}; 